import { getEnglishClaimClients } from "./caseClaimHelper";

export const SUMMARY_CASE_TABS = {
  ALL: "ALL",
  BHP: "BHP",
  ARD: "ARD",
};

// Not a General programme tab: scopes the Businesses tab summary.
export const BUSINESS_SUMMARY_SCOPE = "BUSINESS";

const percentage = (part, total) => {
  if (!total) return 0;
  return Number(((part / total) * 100).toFixed(2));
};

export const deriveClientSummary = (summary = {}) => {
  const totalClients = summary.totalClients || 0;
  const retainersSigned = summary.retainersSigned || 0;
  const questionnairesCompleted = summary.questionnairesCompleted || 0;

  return {
    ...summary,
    totalClients,
    retainersSigned,
    retainersPending: totalClients - retainersSigned,
    retainersSignedPercentage: percentage(retainersSigned, totalClients),
    questionnairesCompleted,
    questionnairesPending: totalClients - questionnairesCompleted,
    questionnairesCompletedPercentage: percentage(
      questionnairesCompleted,
      totalClients,
    ),
    discontinuanceSubmittedPercentage: percentage(
      summary.discontinuanceSubmitted || 0,
      summary.discontinuanceTotal || 0,
    ),
  };
};

const metric = (key, labelKey, value, icon) => ({
  key,
  labelKey,
  value,
  icon,
});

export const SUMMARY_CARD_MAX_COLUMNS = 6;

// Spreads the cards evenly instead of filling rows of six and leaving a
// remainder alone on the last row: 12 -> 6+6, 10 -> 5+5, 7 -> 4+3.
export const getSummaryCardColumns = (
  cardCount,
  maxColumns = SUMMARY_CARD_MAX_COLUMNS,
) => {
  if (!cardCount || cardCount < 1) return maxColumns;
  if (cardCount <= maxColumns) return cardCount;
  return Math.ceil(cardCount / Math.ceil(cardCount / maxColumns));
};

export const getClientSummaryCards = (
  summary,
  caseTab = SUMMARY_CASE_TABS.ALL,
) => {
  const derived = deriveClientSummary(summary);
  const totalLabelKey = {
    [SUMMARY_CASE_TABS.ALL]: "totalClients",
    [SUMMARY_CASE_TABS.BHP]: "bhpClients",
    [SUMMARY_CASE_TABS.ARD]: "ardClients",
    [BUSINESS_SUMMARY_SCOPE]: "businessMatters",
  }[caseTab] || "totalClients";
  const cards = [
    metric("totalClients", totalLabelKey, derived.totalClients, "clients"),
  ];

  if (caseTab === SUMMARY_CASE_TABS.ALL) {
    cards.push(
      metric("ardClients", "ardClients", derived.ardClients || 0, "clients"),
      metric("bhpClients", "bhpClients", getEnglishClaimClients(derived), "clients"),
    );
  }

  cards.push(
    metric("retainersSigned", "retainersSigned", derived.retainersSigned, "retainer"),
    metric("retainersPending", "retainersPending", derived.retainersPending, "retainer"),
    metric(
      "retainersSignedPercentage",
      "retainersSignedPercentage",
      derived.retainersSignedPercentage,
      "retainerPercentage",
    ),
    metric(
      "questionnairesCompleted",
      "questionnairesCompleted",
      derived.questionnairesCompleted,
      "questionnaire",
    ),
    metric(
      "questionnairesPending",
      "questionnairesPending",
      derived.questionnairesPending,
      "questionnaire",
    ),
    metric(
      "questionnairesCompletedPercentage",
      "questionnairesCompletedPercentage",
      derived.questionnairesCompletedPercentage,
      "questionnairePercentage",
    ),
  );

  if (
    caseTab === SUMMARY_CASE_TABS.ALL
    || caseTab === SUMMARY_CASE_TABS.BHP
    || caseTab === BUSINESS_SUMMARY_SCOPE
  ) {
    cards.push(
      metric(
        "discontinuanceTotal",
        caseTab === BUSINESS_SUMMARY_SCOPE
          ? "businessDiscontinuanceTotal"
          : "discontinuanceTotal",
        derived.discontinuanceTotal || 0,
        "questionnaire",
      ),
      metric(
        "discontinuanceSubmitted",
        "disputesSubmitted",
        derived.discontinuanceSubmitted || 0,
        "questionnaire",
      ),
      metric(
        "discontinuanceSubmittedPercentage",
        "disputesSubmittedPercentage",
        derived.discontinuanceSubmittedPercentage,
        "questionnairePercentage",
      ),
    );
  }

  return cards;
};

// Businesses tab cards from the `caseTab=BUSINESS` summary. The company count
// (`businessTotal`) is already in the page header, so it has no card here; the
// summary's `totalClients` counts business matters.
export const getBusinessSummaryCards = (businessSummary = null) => (
  businessSummary?.totalClients !== undefined && businessSummary?.totalClients !== null
    ? getClientSummaryCards(businessSummary, BUSINESS_SUMMARY_SCOPE)
    : []
);
